import React from "react";
import { Divider } from "semantic-ui-react";
import styled from "styled-components";

import {
  BoldSpan,
  DoubleInputGrid,
  FlexDiv,
  GeneralContainer,
  Value,
  QuestionTitle,
} from "../shared";

interface Props {
  additionalInfo: { [key: string]: any };
  publicAccessTitle?: string
}

const EpaPublicAccessibilityDoc: React.FC<Props> = ({ additionalInfo, publicAccessTitle }) => {
  const {
    locationIdentifierChoices,
    locationIdentifierServiceLineYesNo,
    locationIdentifierServiceLineNoExplanation,
    locationIdentifierOtherExplanation,
    inventoryPublicAccessChoices,
    inventoryPublicAccessOtherExplanation,
  } = additionalInfo;

  // Location Identifier Choice Values
  const hasAddressChoice = locationIdentifierChoices?.includes("Address");
  const hasStreetChoice = locationIdentifierChoices?.includes("Street");
  const hasBlockChoice = locationIdentifierChoices?.includes("Block");
  const hasIntersectionChoice =
    locationIdentifierChoices?.includes("Intersection");
  const hasLandmarkChoice = locationIdentifierChoices?.includes("Landmark");
  const hasGPSChoice = locationIdentifierChoices?.includes("GPS Coordinates");
  const hasLocationIdentifierOtherChoice =
    locationIdentifierChoices?.includes("Other");

  // Public Access Choice Values
  const hasInteractiveOnlineMapChoice = inventoryPublicAccessChoices?.includes(
    "Interactive online map"
  );
  const hasStaticOnlineMapChoice =
    inventoryPublicAccessChoices?.includes("Static online map");
  const hasOnlineSpreadsheet =
    inventoryPublicAccessChoices?.includes("Online spreadsheet");
  const hasPrintedMapChoice = inventoryPublicAccessChoices?.includes(
    "Printed service line map"
  );
  const hasPrintedTubularDataChoice = inventoryPublicAccessChoices?.includes(
    "Printed tabular Data"
  );
  const hasMailingOrNewsletterChoice = inventoryPublicAccessChoices?.includes(
    "Information on water utility mailings or newsletter"
  );
  const hasHardCopyChoice = inventoryPublicAccessChoices?.includes(
    "Hard copy information available in water system office"
  );
  const hasPublicAccessOtherChoice =
    inventoryPublicAccessChoices?.includes("Other");

  return (
    <GeneralContainer>
      <div>
        <span>
          Select the location identifiers that you use for your service line
          inventory. Check all that apply.
        </span>
        <DoubleInputGrid>
          <StyledFlexDiv>
            <input type="checkbox" id="addressChoice" checked={hasAddressChoice} />
            <label htmlFor="addressChoice">Address</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="streetChoice" checked={hasStreetChoice} />
            <label htmlFor="streetChoice">Street</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="blockChoice" checked={hasBlockChoice} />
            <label htmlFor="blockChoice">Block</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="intersectionChoice" checked={hasIntersectionChoice} />
            <label htmlFor="intersectionChoice">Intersection</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="landmarkChoice" checked={hasLandmarkChoice} />
            <label htmlFor="landmarkChoice">Landmark</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="gpsChoice" checked={hasGPSChoice} />
            <label htmlFor="gpsChoice">GPS Coordinates</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="locationIdentifierOtherChoice" checked={hasLocationIdentifierOtherChoice} />
            <label htmlFor="locationIdentifierOtherChoice">Other</label>
          </StyledFlexDiv>
        </DoubleInputGrid>
      </div>
      <div>
        <QuestionTitle>If Other, please describe</QuestionTitle>
        <Value>{locationIdentifierOtherExplanation ?? "--"}</Value>
      </div>
      <Divider />
      <div>
        <QuestionTitle>
          <p>
            Does every <BoldSpan>service line</BoldSpan> have a location
            identifier?
          </p>
        </QuestionTitle>
        <Value>{locationIdentifierServiceLineYesNo ?? "--"}</Value>
      </div>
      <div>
        <QuestionTitle>
          If No, explain. Remember that location identifiers are required for
          service lines that are lead and galvanized requiring replacement
        </QuestionTitle>
        <Value>{locationIdentifierServiceLineNoExplanation ?? "--"}</Value>
      </div>
      <Divider />

      <div>
        <QuestionTitle>
        {
            publicAccessTitle ??
            `How are you making your inventory publicly accessible? Check all that
            apply. Remember that if your system serves &gt; 50,000 people, you
            must provide the inventory online.`
        }
        </QuestionTitle>
        <DoubleInputGrid>
          <StyledFlexDiv>
            <input type="checkbox" id="interactiveOnlineMapChoice" checked={hasInteractiveOnlineMapChoice} />
            <label htmlFor="interactiveOnlineMapChoice">Interactive online map</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="staticOnlineMapChoice" checked={hasStaticOnlineMapChoice} />
            <label htmlFor="staticOnlineMapChoice">Static online map</label>
          </StyledFlexDiv>{" "}
          <StyledFlexDiv>
            <input type="checkbox" id="onlineSpreadsheet" checked={hasOnlineSpreadsheet} />
            <label htmlFor="onlineSpreadsheet">Online Spreadsheet</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="printedMapChoice" checked={hasPrintedMapChoice} />
            <label htmlFor="printedMapChoice">Printed service line map</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="printedTubularDataChoice" checked={hasPrintedTubularDataChoice} />
            <label htmlFor="printedTubularDataChoice">Printed tabular Data</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="mailingOrNewsletterChoice" checked={hasMailingOrNewsletterChoice} />
            <label htmlFor="mailingOrNewsletterChoice">Information on water utility mailings or newsletter</label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="hardCopyChoice" checked={hasHardCopyChoice} />
            <label htmlFor="hardCopyChoice">
              Hard copy information available in water system office
            </label>
          </StyledFlexDiv>
          <StyledFlexDiv>
            <input type="checkbox" id="publicAccessOtherChoice" checked={hasPublicAccessOtherChoice} />
            <label htmlFor="publicAccessOtherChoice">Other</label>
          </StyledFlexDiv>
        </DoubleInputGrid>
      </div>
      <Divider />
      <div>
        <QuestionTitle>If Other, please describe</QuestionTitle>
        <Value>{inventoryPublicAccessOtherExplanation ?? "--"}</Value>
      </div>
    </GeneralContainer>
  );
};

const StyledFlexDiv = styled(FlexDiv)`
  pointer-events: none;
`;

export default EpaPublicAccessibilityDoc;
